/* eslint-disable react/jsx-props-no-spreading */
import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Radio, Select } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetDataQuery, useGetFormsQuery, useUpdateDataMutation } from '../api/v2/forms'
import useActionLog from '../hooks/useActionLog'
import useTranslations from '../hooks/useTranslations'
import { useMyContext } from '../context/context'
import PhoneNumber from './PhoneNumber'

const capitalizeWords = str => str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase())

const toUpperCase = str => str.toUpperCase()

function Component(props) {
  const { model, value, onChange, onlyRead, form } = props

  const [valueOld] = useState(value)

  const { event } = useMyContext()
  const { t } = useTranslations()

  // Le format canonique d'un champ à options est l'OBJET { clé: true } (écrit ci-dessous).
  // Certaines valeurs héritées sont stockées en CHAÎNE (widget WordPress, BO avant correctif,
  // import) : sans ce distinguo, Object.keys('14') itérait les caractères et le champ
  // s'affichait pré-rempli avec « 0 ».
  const valuesArray = useMemo(() => {
    if (value == null || value === '') return []
    if (Array.isArray(value)) return value
    if (typeof value === 'string') return [value]
    return Object.keys(value).filter(k => value[k])
  }, [value])

  const valueString = useMemo(() => {
    if (value == null || value === '') return null
    if (typeof value === 'string') return value
    const arr = Object.keys(value).filter(k => value[k])
    return arr.length ? arr[0] : null
  }, [value])

  const onChangeValuesArray = useCallback(
    e => {
      const v = e?.target?.value || e
      const newV = v.reduce((prev, curr) => ({ ...prev, [curr]: true }), {})
      onChange(Object.keys(newV).length === 0 ? undefined : newV)
    },
    [onChange]
  )

  const onChangeValueString = useCallback(
    e => {
      const v = e?.target?.value || e
      onChange({ [v]: true })
    },
    [onChange]
  )

  const disabled = useMemo(() => (model.model === 'UE' && !event?.id) || (valueOld && model.isDisabled) || onlyRead, [
    model,
    event,
    valueOld,
    onlyRead,
  ])

  const uniqueModelValues = useMemo(() => (model.values || []).filter((m, idx, arr) => arr.findIndex(v => v.id === m.id) === idx), [
    model.values,
  ])

  const values = useMemo(
    () => uniqueModelValues.map(m => ({ ...m, label: t(`${form.id}-${model.modelId}-${m.id}`, { valueDefault: m.label }) })),
    [form, model.modelId, uniqueModelValues, t]
  )

  if (model.component === 'phone-number') {
    return <PhoneNumber {...props} disabled={disabled} />
  }

  if (model.component === 'textarea') {
    return <Input.TextArea {...props} rows={3} disabled={disabled} />
  }

  if (model.component === 'input-number') {
    return <InputNumber {...props} disabled={disabled} />
  }

  if (model.component === 'input') {
    return <Input {...props} type={model.key === 'email' ? 'email' : 'text'} disabled={disabled} />
  }

  if (model.component === 'select') {
    return <Select {...props} value={valueString} onChange={onChangeValueString} options={values} disabled={disabled} />
  }

  if (model.component === 'select-multiple') {
    return <Select mode="multiple" {...props} value={valuesArray} onChange={onChangeValuesArray} options={values} disabled={disabled} />
  }

  const radioCheckStyles = model.metadata?.valuesColumn ? { flexDirection: 'column', display: 'flex', marginTop: 6, gap: 4 } : {}

  if (model.component === 'radio') {
    return (
      <Radio.Group
        style={radioCheckStyles}
        {...props}
        value={valueString}
        onChange={onChangeValueString}
        options={values}
        disabled={disabled}
      />
    )
  }

  if (model.component === 'checkbox') {
    return (
      <Checkbox.Group
        style={radioCheckStyles}
        {...props}
        value={valuesArray}
        onChange={onChangeValuesArray}
        options={values}
        disabled={disabled}
      />
    )
  }

  return <div />
}

function FormItemContent({ form, model, onlyRead, itemIndex }) {
  const { t } = useTranslations()
  const itemClassName = model.metadata?.css ? `gayakoa-item-${form.id}-${itemIndex}` : null

  const wrap = content =>
    itemClassName ? (
      <>
        <style>{`.${itemClassName} { ${model.metadata.css} }`}</style>
        <div className={itemClassName}>{content}</div>
      </>
    ) : (
      content
    )

  if (model.component === 'comment') {
    const source = model.metadata?.comment || ''
    const uid = model.metadata?.uid
    const translated = source && uid ? t(`${form.id}-fm-${uid}`, { valueDefault: source }) : source
    return wrap(<Form.Item>{translated}</Form.Item>)
  }

  if (model.component === 'html') {
    const source = model.metadata?.html || ''
    const uid = model.metadata?.uid
    const translated = source && uid ? t(`${form.id}-fm-${uid}`, { valueDefault: source }) : source
    return wrap(
      <Form.Item>
        <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: translated }} />
      </Form.Item>
    )
  }

  return wrap(
    <Form.Item
      label={t(`${form.id}-${model.modelId}`, { valueDefault: model.label || model.value })}
      name={model.isExtended ? [model.model, 'extended', model.key] : [model.model, model.key]}
      preserve={false}
      rules={[
        ...(model.key === 'email' ? [{ type: 'email', message: "Le format de l'email est incorrect" }] : []),
        ...(model.isRequired
          ? [
              {
                required: model.isRequired,
                message: t('FORM_ITEM_REQUIRED_MESSAGE', {
                  values: [t(`${form.id}-${model.modelId}`, { valueDefault: model.label || model.value })],
                }),
              },
            ]
          : []),
      ]}
      normalize={value => {
        if (model.format === 'capitalize-words') {
          return capitalizeWords(value)
        }
        if (model.format === 'uppercase') {
          return toUpperCase(value)
        }
        return value
      }}
    >
      <Component model={model} onlyRead={onlyRead} form={form} />
    </Form.Item>
  )
}

function FormItem({ form, model, onlyRead, itemIndex }) {
  if (model.metadata?.conditionModelId && model.metadata?.conditionKeys?.length) {
    const parentModel = form.models.find(m => m.modelId === model.metadata.conditionModelId)
    if (parentModel) {
      const parentFieldName = parentModel.isExtended
        ? [parentModel.model, 'extended', parentModel.key]
        : [parentModel.model, parentModel.key]

      return (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const parentValue = getFieldValue(parentFieldName)
            let selectedKeys = []
            if (parentValue && typeof parentValue === 'object') {
              selectedKeys = Object.keys(parentValue).filter(k => parentValue[k])
            } else if (typeof parentValue === 'string') {
              selectedKeys = [parentValue]
            }
            const isVisible = model.metadata.conditionKeys.some(k => selectedKeys.includes(k))
            if (!isVisible) return null
            return <FormItemContent form={form} model={model} onlyRead={onlyRead} itemIndex={itemIndex} />
          }}
        </Form.Item>
      )
    }
  }

  return <FormItemContent form={form} model={model} onlyRead={onlyRead} itemIndex={itemIndex} />
}

const getModelValue = (body, model) => {
  const section = model.isExtended ? body?.[model.model]?.extended : body?.[model.model]
  return section?.[model.key]
}

const clearModelValue = (body, model) => {
  if (!body[model.model]) body[model.model] = {}
  if (model.isExtended) {
    if (!body[model.model].extended) body[model.model].extended = {}
    if (body[model.model].extended[model.key] === null) return false
    body[model.model].extended[model.key] = null
    return true
  }
  if (body[model.model][model.key] === null) return false
  body[model.model][model.key] = null
  return true
}

const matchesCondition = (parentValue, conditionKeys) => {
  let selectedKeys = []
  if (parentValue && typeof parentValue === 'object') {
    selectedKeys = Object.keys(parentValue).filter(k => parentValue[k])
  } else if (typeof parentValue === 'string') {
    selectedKeys = [parentValue]
  }
  return conditionKeys.some(k => selectedKeys.includes(k))
}

const cleanHiddenConditionalValues = (body, models) => {
  const cleaned = JSON.parse(JSON.stringify(body || {}))
  let changed = true
  while (changed) {
    changed = false
    for (const model of models || []) {
      const cond = model.metadata
      if (!cond?.conditionModelId || !cond?.conditionKeys?.length) continue
      const parentModel = models.find(m => m.modelId === cond.conditionModelId)
      if (!parentModel) continue
      const parentValue = getModelValue(cleaned, parentModel)
      if (!matchesCondition(parentValue, cond.conditionKeys)) {
        if (clearModelValue(cleaned, model)) changed = true
      }
    }
  }
  return cleaned
}

function deepMerge(objA, objB) {
  const result = { ...objA }

  for (const key in objB) {
    if (objB[key] !== null && typeof objB[key] === 'object' && !Array.isArray(objB[key])) {
      result[key] = deepMerge(objA[key] || {}, objB[key])
    } else {
      result[key] = objB[key]
    }
  }

  return result
}

export default function FormBuilder({
  children,
  formRef,
  formIdDefault,
  userId,
  companyId,
  title,
  withModal,
  onFinish,
  Buttons,
  onlyRead,
  onAfterInit,
  force,
  values,
}) {
  const [open, setOpen] = useState(false)
  const [formId, setFormId] = useState()
  const [loading, setLoading] = useState(false)

  const { myContext } = useMyContext()
  const [ff] = Form.useForm()
  const { t } = useTranslations()
  const { addLog } = useActionLog()

  const { data: forms } = useGetFormsQuery({ ...myContext })
  const { currentData: formData, isFetching } = useGetDataQuery({ ...myContext, userId, companyId })
  const [update, { error }] = useUpdateDataMutation()

  const data = useMemo(() => deepMerge(formData || {}, values || {}), [formData, values])

  const form = useMemo(() => formRef || ff, [ff, formRef])

  const currentForm = useMemo(() => {
    if (forms) {
      const find = forms?.find(f => f.id === formId)
      if (find) {
        return find
      }
    }
    return null
  }, [forms, formId])

  useEffect(() => {
    if (formIdDefault) {
      setFormId(formIdDefault)
    }
  }, [formIdDefault])

  const initKeyRef = useRef(null)
  const currentKey = `${userId ?? ''}-${companyId ?? ''}`

  useEffect(() => {
    if (withModal && !open) {
      initKeyRef.current = null
      return
    }
    if (formData !== undefined && !isFetching && initKeyRef.current !== currentKey) {
      initKeyRef.current = currentKey
      form.resetFields()
      form.setFieldsValue(data)
      if (onAfterInit) {
        onAfterInit()
      }
    }
  }, [formData, isFetching, data, form, onAfterInit, open, withModal, currentKey])

  const Builder = useCallback(
    () => (
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
        {currentForm && (
          <>
            {currentForm.css && <style>{`.gayakoa-form-${currentForm.id} { ${currentForm.css} }`}</style>}
            <Form
              className={currentForm.css ? `gayakoa-form-${currentForm.id}` : undefined}
              style={{ flexGrow: 1 }}
              labelWrap
              form={form}
              layout={currentForm.layout}
              onFinish={async body => {
                try {
                  setLoading(true)
                  if (companyId) {
                    addLog({ actionId: 31, companyId })
                  }
                  if (userId) {
                    addLog({ actionId: 32, userId })
                  }
                  const v = deepMerge({ C: { siren: values?.C?.siren || '' } }, body || {})
                  const cleaned = cleanHiddenConditionalValues(v, currentForm.models)
                  const result = await update({
                    ...myContext,
                    userId,
                    companyId,
                    body: { ...cleaned, force },
                  }).unwrap()
                  if (onFinish) {
                    await onFinish(result)
                  }
                  setOpen(false)
                } catch (err) {
                  console.error(err)
                } finally {
                  setLoading(false)
                }
              }}
            >
              {currentForm.models.map((m, mi) => (
                <FormItem form={currentForm} model={m} onlyRead={onlyRead} itemIndex={mi} />
              ))}
              {error && (
                <Form.Item>
                  <Alert type="error" message={t(error?.data?.message)} showIcon />
                </Form.Item>
              )}
              {!onlyRead && (
                <>
                  {Buttons ? (
                    <Buttons htmlType="submit" loading={loading} />
                  ) : (
                    <div
                      style={{
                        position: 'sticky',
                        display: 'flex',
                        gap: 8,
                        bottom: 0,
                        backgroundColor: 'white',
                        paddingTop: 8,
                      }}
                    >
                      {/* <Button onClick={() => setOpen(false)}>Annuler</Button> */}
                      <Button type="primary" htmlType="submit" style={{ flexGrow: 1 }} loading={loading}>
                        Enregistrer
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form>
          </>
        )}
      </div>
    ),
    [currentForm, form, error, t, onlyRead, Buttons, loading, companyId, userId, update, myContext, force, values, onFinish, addLog]
  )

  if (!withModal) {
    return Builder()
  }

  return (
    <>
      {React.cloneElement(children, { onClick: () => setOpen(true) })}
      <Modal
        title={title || ' '}
        open={open}
        footer={null}
        onCancel={() => setOpen(false)}
        bodyStyle={{
          maxHeight: 'calc(100vh - 300px)',
          overflowY: 'auto',
        }}
      >
        {Builder()}
      </Modal>
    </>
  )
}
